import React, { useEffect, useState, useMemo } from 'react';
import { Activity, Globe2, RefreshCw, MousePointer, TrendingUp, Users } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { divIcon, latLngBounds } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchAdminAnalyticsEvents, isSupabaseAnalyticsEnabled, EventRecord } from '../../services/supabaseAnalytics';

type MapPoint = { lat: number; lng: number; label: string; country: string; count: number; sessions: Set<string> };

// ── Geo helpers ───────────────────────────────────────────────────────────────
// For new rows, geo lives in top-level columns (city, country, lat, lng …).
// For old rows it's inside the metadata JSON blob. Both paths are tried.

function getGeoField(e: EventRecord, key: 'city' | 'region' | 'country' | 'country_code'): string {
  // Top-level column first
  const col = e[key];
  if (col && typeof col === 'string') return col;
  // Fallback: metadata blob
  try {
    const v = e.metadata?.[key];
    return typeof v === 'string' ? v : '';
  } catch { return ''; }
}

function getLatLng(e: EventRecord): { lat: number | null; lng: number | null } {
  // Top-level columns first (stored as numbers by Supabase)
  if (typeof e.lat === 'number' && typeof e.lng === 'number') {
    return { lat: e.lat, lng: e.lng };
  }
  // Fallback: metadata blob (stored as number or numeric string)
  try {
    const rawLat = e.metadata?.['lat'];
    const rawLng = e.metadata?.['lng'];
    const lat = typeof rawLat === 'number' ? rawLat : parseFloat(String(rawLat ?? ''));
    const lng = typeof rawLng === 'number' ? rawLng : parseFloat(String(rawLng ?? ''));
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  } catch { /* fall through */ }
  return { lat: null, lng: null };
}

function getMeta(e: EventRecord, key: string): string {
  try {
    const v = e.metadata?.[key];
    if (typeof v === 'number') return String(v);
    return typeof v === 'string' ? v : '';
  } catch { return ''; }
}

// ── Formatters / aggregators ──────────────────────────────────────────────────

function formatDate(value?: string): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return value; }
}

function countBy(items: EventRecord[], key: (e: EventRecord) => string): { label: string; count: number }[] {
  const map = new Map<string, number>();
  for (const e of items) {
    try { const k = key(e) || 'Unknown'; map.set(k, (map.get(k) || 0) + 1); } catch { /* skip */ }
  }
  return Array.from(map.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
}

// ── UI primitives ─────────────────────────────────────────────────────────────

function Bar({ label, count, max, color = '#7e6844' }: { label: string; count: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 4;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 32px', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 12, color: '#6c5532', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ height: 6, background: '#efe5d3', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
        </div>
      </div>
      <span style={{ textAlign: 'right', fontSize: 11, color: '#8a7451' }}>{count}</span>
    </div>
  );
}

function Card({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ border: '1px solid #d8c9af', background: '#fcf6eb', borderRadius: 6, padding: '12px 16px' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#8a7451', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#6c5532' }}>{icon}</span>{label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, color: '#5b4727', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9b8766', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid #d8c9af', background: '#fffaf2', borderRadius: 6, padding: 16 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#8a7451', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Visitor Map ───────────────────────────────────────────────────────────────

function FitMap({ points }: { points: MapPoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) { map.setView([30, 0], 2); return; }
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 6); return; }
    const bounds = latLngBounds(points.map(p => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [map, points]);
  return null;
}

function VisitorMap({ points }: { points: MapPoint[] }) {
  if (points.length === 0) {
    return (
      <Section title="Visitor map">
        <div style={{ padding: '32px 0', textAlign: 'center', fontSize: 12, color: '#9b8766' }}>
          No geo data yet — coordinates will appear here once visitors are tracked.
        </div>
      </Section>
    );
  }

  return (
    <Section title="Visitor map">
      <MapContainer center={[30, 0]} zoom={2} scrollWheelZoom={false} style={{ height: 340, width: '100%', borderRadius: 4 }}>
        <TileLayer
          attribution='© OpenStreetMap © CARTO'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <FitMap points={points} />
        {points.map(pt => {
          const r = Math.max(10, Math.min(26, 10 + pt.count * 3));
          const icon = divIcon({
            className: '',
            html: `<div style="
              width:${r * 2}px;height:${r * 2}px;border-radius:50%;
              background:rgba(126,104,68,0.82);border:2px solid #fff;
              display:flex;align-items:center;justify-content:center;
              font-size:${Math.max(10, r - 3)}px;font-weight:700;color:#fff;
              box-shadow:0 2px 8px rgba(91,71,39,0.35);
            ">${pt.count > 1 ? pt.count : ''}</div>`,
            iconSize: [r * 2, r * 2],
            iconAnchor: [r, r],
          });
          return (
            <Marker key={`${pt.lat}-${pt.lng}`} position={[pt.lat, pt.lng]} icon={icon}>
              <Popup>
                <div style={{ minWidth: 140 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#3b2a14' }}>
                    {pt.label}{pt.country ? `, ${pt.country}` : ''}
                  </div>
                  <div style={{ fontSize: 11, color: '#8a7451', marginTop: 3 }}>
                    {pt.count} event{pt.count !== 1 ? 's' : ''} · {pt.sessions.size} session{pt.sessions.size !== 1 ? 's' : ''}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = { page_view: '#dbeafe', click: '#fef3c7', admin_dashboard_view: '#f3f4f6' };
const EVENT_TEXT:   Record<string, string> = { page_view: '#1e40af', click: '#92400e', admin_dashboard_view: '#4b5563' };

const VisitorAnalytics: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminAnalyticsEvents(500);
      setEvents(Array.isArray(data) ? data : []);
      setLastUpdated(new Date().toLocaleString());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    try {
      const pageViews = events.filter(e => e.event_type === 'page_view');
      const clicks    = events.filter(e => e.event_type === 'click');

      // Sessions
      const uniqueSessions = new Set(
        events.map(e => getMeta(e, 'session_id')).filter(Boolean)
      ).size;

      // Geo — new rows: top-level columns. Old rows: metadata blob.
      // An event has usable geo if it has a non-empty city or country.
      const geoEvents = events.filter(e => {
        const city    = getGeoField(e, 'city');
        const country = getGeoField(e, 'country');
        return (city && city !== 'Unknown') || (country && country !== 'Unknown');
      });

      const locations = countBy(geoEvents, e => {
        const city   = getGeoField(e, 'city');
        const region = getGeoField(e, 'region');
        if (city && region) return `${city}, ${region}`;
        return city || getGeoField(e, 'country') || 'Unknown';
      });

      const countries = countBy(
        events.filter(e => getGeoField(e, 'country')),
        e => getGeoField(e, 'country')
      );

      // Map points — cluster by rounded lat/lng
      const mapPoints = (() => {
        const bucket = new Map<string, MapPoint>();
        for (const e of events) {
          const { lat, lng } = getLatLng(e);
          if (lat === null || lng === null) continue;
          const city    = getGeoField(e, 'city')    || getMeta(e, 'location') || 'Unknown';
          const country = getGeoField(e, 'country') || getGeoField(e, 'country_code') || '';
          const sid     = getMeta(e, 'session_id');
          const key     = `${lat.toFixed(2)},${lng.toFixed(2)}`;
          const existing = bucket.get(key);
          if (existing) {
            existing.count++;
            if (sid) existing.sessions.add(sid);
          } else {
            bucket.set(key, { lat, lng, label: city, country, count: 1, sessions: new Set(sid ? [sid] : []) });
          }
        }
        return Array.from(bucket.values()).sort((a, b) => b.count - a.count);
      })();

      const browsers    = countBy(events.filter(e => getMeta(e, 'browser')), e => getMeta(e, 'browser'));
      const devices     = countBy(events.filter(e => getMeta(e, 'device')),  e => getMeta(e, 'device'));
      const pages       = countBy(pageViews, e => e.page || getMeta(e, 'page') || '—');
      const clickLabels = countBy(clicks, e => e.label || '—').slice(0, 10);
      const referrers   = countBy(
        events.filter(e => { const r = getMeta(e, 'referrer'); return r && r !== 'Direct'; }),
        e => getMeta(e, 'referrer')
      );

      const recent = [...events].sort((a, b) => {
        try { return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(); }
        catch { return 0; }
      }).slice(0, 30);

      const now = Date.now();
      const days = Array.from({ length: 14 }, (_, i) => {
        const d = new Date(now - (13 - i) * 86400000);
        const dateStr = d.toISOString().slice(0, 10);
        return {
          label: d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
          count: events.filter(e => (e.created_at || '').slice(0, 10) === dateStr).length,
        };
      });
      const dayMax = days.reduce((m, d) => Math.max(m, d.count), 1);

      return {
        total: events.length,
        pageViews: pageViews.length,
        clicks: clicks.length,
        uniqueSessions,
        uniqueLocations: new Set(geoEvents.map(e => getGeoField(e, 'city') || getGeoField(e, 'country'))).size,
        locations, countries, mapPoints,
        browsers, devices, pages, clickLabels, referrers,
        recent, days, dayMax,
      };
    } catch (e) {
      console.error('VisitorAnalytics stats error:', e);
      return null;
    }
  }, [events]);

  if (!isSupabaseAnalyticsEnabled()) {
    return <div style={{ padding: 32, color: '#9b8766' }}>Supabase not configured.</div>;
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '32px 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {onBack && (
            <button onClick={onBack} style={{ fontSize: 12, color: '#8a7451', background: 'none', border: 'none', cursor: 'pointer' }}>
              ← Dashboard
            </button>
          )}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#8a7451' }}>Rare Book Catalog</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#3b2a14', margin: 0 }}>Visitor Analytics</h1>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && <span style={{ fontSize: 11, color: '#9b8766' }}>Updated {lastUpdated}</span>}
          <button
            onClick={load}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #c9a96e', background: '#fcf6eb', padding: '6px 12px', borderRadius: 4, fontSize: 12, color: '#6c5532', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            <RefreshCw size={13} />{loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#b91c1c', fontSize: 13 }}>
          Error: {error}
        </div>
      )}

      {loading && events.length === 0 ? (
        <div style={{ padding: 64, textAlign: 'center', color: '#9b8766' }}>Loading analytics…</div>
      ) : events.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9b8766', border: '1px solid #d8c9af', borderRadius: 6 }}>No events found.</div>
      ) : !stats ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#b91c1c' }}>Stats error — check console.</div>
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
            <Card icon={<Activity size={14} />}     label="Total Events"   value={stats.total}           sub="all time" />
            <Card icon={<TrendingUp size={14} />}   label="Page Views"     value={stats.pageViews}       sub="page_view events" />
            <Card icon={<MousePointer size={14} />} label="Clicks"         value={stats.clicks}          sub="click events" />
            <Card icon={<Users size={14} />}        label="Sessions"       value={stats.uniqueSessions}  sub="unique sessions" />
            <Card icon={<Globe2 size={14} />}       label="Locations"      value={stats.uniqueLocations} sub="unique cities" />
          </div>

          {/* Activity sparkline */}
          <Section title="Activity — last 14 days">
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 72 }}>
              {stats.days.map((d, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div
                    style={{ width: '100%', borderRadius: '3px 3px 0 0', background: d.count > 0 ? '#7e6844' : '#efe5d3', height: Math.max(4, Math.round((d.count / stats.dayMax) * 56)) }}
                    title={`${d.label}: ${d.count}`}
                  />
                  {i % 3 === 0 && <span style={{ fontSize: 9, color: '#9b8766', whiteSpace: 'nowrap' }}>{d.label}</span>}
                </div>
              ))}
            </div>
          </Section>

          {/* Map */}
          <VisitorMap points={stats.mapPoints} />

          {/* Bar chart panels */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            <Section title="Pages visited">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.pages.length === 0
                  ? <p style={{ fontSize: 12, color: '#9b8766' }}>No page view events yet.</p>
                  : stats.pages.slice(0, 8).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.pages[0]?.count || 1} />)}
              </div>
            </Section>
            <Section title="Cities">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.locations.length === 0
                  ? <p style={{ fontSize: 12, color: '#9b8766' }}>No location data yet.</p>
                  : stats.locations.slice(0, 8).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.locations[0]?.count || 1} color="#5a7c65" />)}
              </div>
            </Section>
            <Section title="Countries">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.countries.length === 0
                  ? <p style={{ fontSize: 12, color: '#9b8766' }}>No country data yet.</p>
                  : stats.countries.slice(0, 8).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.countries[0]?.count || 1} color="#5a7c65" />)}
              </div>
            </Section>
            <Section title="Referrers">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.referrers.length === 0
                  ? <p style={{ fontSize: 12, color: '#9b8766' }}>All traffic is direct.</p>
                  : stats.referrers.slice(0, 8).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.referrers[0]?.count || 1} color="#3561b8" />)}
              </div>
            </Section>
            <Section title="Browsers">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.browsers.slice(0, 6).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.browsers[0]?.count || 1} color="#8b5e3c" />)}
              </div>
            </Section>
            <Section title="Devices">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.devices.slice(0, 4).map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.devices[0]?.count || 1} />)}
              </div>
            </Section>
            <Section title="Top clicks">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.clickLabels.length === 0
                  ? <p style={{ fontSize: 12, color: '#9b8766' }}>No click events yet.</p>
                  : stats.clickLabels.map(({ label, count }) => <Bar key={label} label={label} count={count} max={stats.clickLabels[0]?.count || 1} color="#b85c38" />)}
              </div>
            </Section>
          </div>

          {/* Recent events table */}
          <Section title="Recent events">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e6d8c0' }}>
                    {['Time','Event','Page','City','Country','Session','Browser','Device','Referrer'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#8a7451', fontWeight: 500, textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.recent.map((e, i) => {
                    const type    = e.event_type || '';
                    // Read geo: top-level columns first, metadata fallback
                    const city    = getGeoField(e, 'city')    || getMeta(e, 'location') || '—';
                    const country = getGeoField(e, 'country') || getGeoField(e, 'country_code') || '—';
                    const sid     = getMeta(e, 'session_id');
                    return (
                      <tr key={String(e.id ?? i)} style={{ borderBottom: '1px solid #f0e6d6', color: '#5b4727' }}>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{formatDate(e.created_at)}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{ background: EVENT_COLORS[type] || '#f3f4f6', color: EVENT_TEXT[type] || '#4b5563', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 500 }}>
                            {type || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px' }}>{e.page || getMeta(e, 'page') || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{city}</td>
                        <td style={{ padding: '6px 10px' }}>{country}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 10, color: '#9b8766' }}>{sid ? sid.slice(0, 10) : '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{getMeta(e, 'browser') || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{getMeta(e, 'device') || '—'}</td>
                        <td style={{ padding: '6px 10px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getMeta(e, 'referrer') || 'Direct'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default VisitorAnalytics;
